import { sql as kyselySql } from "kysely";
import { BaseError, formatEther } from "viem";
import { arbitrum, base, baseSepolia, sepolia } from "viem/chains";
import { z } from "zod";
import { MAX_FLOAT_ETH, MAX_NUM_TOKENS_PER_ASSET } from "./config";
import { getCountryName } from "./countries";
// Map value from [low1, high1] into [low2, high2]
export function mapRange(value, low1, high1, low2, high2) {
    return low2 + ((high2 - low2) * (value - low1)) / (high1 - low1);
}
export function quantityToPercent(quantity) {
    return quantity / (MAX_NUM_TOKENS_PER_ASSET / 100);
}
/**
 * @param ownedFractions
 * @returns string
 * @description Calculates the percentage (1/100) of (owned) fractions (1/1000000) and returns it formated
 * @example 1000000 fractions = 100.00%, 1 fraction = 0.0001%, 100 fractions = 0.01%
 */
export const formatFraction = (ownedFractions, locale) => {
    return quantityToPercent(ownedFractions).toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    });
};
const toFixedLocaleString = (value, fractionDigits, locale) => {
    let number = value;
    if (typeof number === "string") {
        number = Number.parseFloat(number);
    }
    return value.toLocaleString(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
};
export const removeOutdatedTimestamps = (timestampedObjects, cutoffInMonths) => {
    // Get the current date and time
    const now = new Date();
    // Calculate the cutoff date and time, based on the cutoffInMonths parameter
    const cutoff = new Date(now.getTime() - cutoffInMonths * 30 * 24 * 60 * 60 * 1000);
    // Filter the array of timestamped objects to keep only those with a timestamp after the cutoff
    return timestampedObjects.filter((object) => object.timestamp != null && new Date(object.timestamp) > cutoff);
};
export const filterTimestampedObjects = (timestampedObjects) => {
    // Create an object to store the filtered lists
    const filteredLists = {
        alltime: timestampedObjects,
        months: removeOutdatedTimestamps(timestampedObjects, 6),
        days: removeOutdatedTimestamps(timestampedObjects, 1),
    };
    // Return the object of filtered lists
    return filteredLists;
};
export const formatWeiToFixedEther = (value, locale) => {
    return toFixedLocaleString(+formatEther(value !== undefined ? BigInt(value.toString()) : 0n), MAX_FLOAT_ETH, locale);
};
export const getThumbnailUrl = (hash) => {
    if (hash.startsWith("http")) {
        return hash;
    }
    if (hash.startsWith("ipfs://")) {
        return `https://zoeovucvjce1ksti.public.blob.vercel-storage.com/ipfs/${hash.slice(7)}`;
    }
    return `https://zoeovucvjce1ksti.public.blob.vercel-storage.com/ipfs/${hash}`;
};
export const getAvatarUrl = (id) => {
    if (id.startsWith("http")) {
        return id;
    }
    return `https://zoeovucvjce1ksti.public.blob.vercel-storage.com/avatars/${id}`;
};
export const shortenId = (id, keepCharactersAtStart = 6, keepCharactersAtEnd = 3) => {
    return `${id.slice(0, keepCharactersAtStart)}...${id.slice(id.length - keepCharactersAtEnd, id.length)}`;
};
export async function copyTextToClipboard(text) {
    await navigator.clipboard.writeText(text);
}
export function getEtherscanTxLink(transactionHash) {
    const explorer = getChain().blockExplorers.default.url;
    return `${explorer}/tx/${transactionHash}`;
}
export function getEtherscanAddressLink(address) {
    const explorer = getChain().blockExplorers.default.url;
    return `${explorer}/address/${address}`;
}
export function getEtherscanTokenIdLink(contractAddress, tokenId) {
    const explorer = getChain().blockExplorers.default.url;
    return `${explorer}/token/${contractAddress}?a=${tokenId}`;
}
export function getSlugFromChain(chain) {
    return chain.name.toLowerCase().replace(" ", "-").replace("-one", "");
}
export function getChainFromSlug(slug) {
    const chains = [arbitrum, base, baseSepolia, sepolia];
    const chain = chains.find((chain) => getSlugFromChain(chain) === slug);
    if (!chain) {
        throw new Error("Invalid slug");
    }
    return chain;
}
export function getEtherscanApiUrl() {
    const chain = getChain();
    if (chain.name === "Sepolia") {
        return "https://api-sepolia.etherscan.io/api?apikey=D5NW6DAMXTJ9PMISH6UB9GXCNGWF2AY2NV";
    }
    if (chain.name === "Arbitrum One") {
        return "https://api.arbiscan.io/api?apikey=9J7FBX5EJBVSDDZGDWRPER8Z56I87NHUPU";
    }
    if (chain.name === "Base" || chain.name === "Base Sepolia") {
        return `${chain.blockExplorers.default.apiUrl}?apikey=5WNDQ22CMDJIEQNEM7YE32HN3KTYKEFFUG}`;
    }
    throw new Error("Invalid network");
}
export function getChain() {
    const network = process.env.NEXT_PUBLIC_BLOCKCHAIN_NETWORK;
    if (network === "arbitrum") {
        return arbitrum;
    }
    if (network === "sepolia") {
        return sepolia;
    }
    if (network === "base") {
        return base;
    }
    if (network === "base-sepolia") {
        return baseSepolia;
    }
    throw new Error("Invalid network");
}
export function getBlockchainRpc(chain) {
    if (chain.name === "Sepolia") {
        return `https://eth-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY ?? ""}`;
    }
    if (chain.name === "Arbitrum One") {
        return `https://arb-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY ?? ""}`;
    }
    if (chain.name === "Base") {
        return `https://base-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY ?? ""}`;
    }
    if (chain.name === "Base Sepolia") {
        return `https://base-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY ?? ""}`;
    }
    throw new Error(`Network not found: ${getChain().name}}`);
}
export function truncateNumber(num, decimalPlaces) {
    const factor = 10 ** decimalPlaces;
    return Math.floor(num * factor) / factor;
}
export function getErrorMessage(error) {
    if (error instanceof BaseError) {
        if (error.shortMessage.startsWith("The total cost")) {
            return "You need more ETH in your wallet for this transaction";
        }
        return error.shortMessage;
    }
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === "object" && error !== null && "message" in error && typeof error.message === "string") {
        return error.message;
    }
    if (typeof error === "string") {
        return error;
    }
    try {
        return `An unexpected error occurred: ${JSON.stringify(error)}`;
    }
    catch {
        return "An unexpected and non-serializable error occurred.";
    }
}
export function objectToQueryParam(key, value) {
    const params = new URLSearchParams();
    params.set(key, JSON.stringify(value));
    return params.toString();
}
export const talentirChain = z.enum(["arbitrum", "sepolia", "base", "base-sepolia"]);
export function getZeroDevProjectId(chain) {
    if (chain.name === "Sepolia") {
        return "edded4e6-89c6-4f73-be4a-0a2d189373f2";
    }
    if (chain.name === "Arbitrum One") {
        return "d846d5af-8f9e-46e9-aa2d-4eee7554ef5d";
    }
    if (chain.name === "Base") {
        return "e8247ccc-b1ed-45a8-87f3-b7bd46ab187b";
    }
    if (chain.name === "Base Sepolia") {
        return "d8a3e954-0e65-4b52-88c5-0b7e67c27a21";
    }
    throw new Error(`Network not found: ${getChain().name}}`);
}
// use in conjunction with drizzle.toSQL()
export function replaceParamsInQueryDrizzle({ sql, params }) {
    const result = sql.replace(/\$(\d+)/g, (_, index) => {
        const param = params[Number.parseInt(index) - 1];
        if (typeof param === "string") {
            return `'${param}'`;
        }
        return String(param);
    });
    // remove line breaks and spaces
    return result.replace(/\n/g, "").replace(/\s+/g, " ");
}
export function replaceParamsInQueryKysely({ sql, parameters, }) {
    const result = sql.replace(/\$(\d+)/g, (_, index) => {
        const param = parameters[Number.parseInt(index) - 1];
        if (typeof param === "string") {
            return `'${param}'`;
        }
        return String(param);
    });
    return result;
}
export function truncateEthAddress(address) {
    const truncateRegex = /^(0x[a-zA-Z0-9]{4})[a-zA-Z0-9]+([a-zA-Z0-9]{4})$/;
    const match = address.match(truncateRegex);
    if (!match)
        return address;
    return `${match[1]}…${match[2]}`;
}
export function jsonAggAs(expr, as) {
    function camelToSnakeCase(str) {
        return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    }
    const asSnakeCase = camelToSnakeCase(as);
    const sqlExpression = kyselySql `(select coalesce(json_agg(agg), '[]') as ${kyselySql.raw(asSnakeCase)} from ${expr} as agg)`;
    return sqlExpression.as(as);
}
export async function tryCatch(promise) {
    try {
        const data = await promise;
        return { data, error: null };
    }
    catch (error) {
        return { data: null, error: error };
    }
}
export function tryCatchSync(fn) {
    try {
        const data = fn();
        return { data, error: null };
    }
    catch (error) {
        return { data: null, error: error };
    }
}
export function chunkArray(array, chunkSize) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        const chunk = array.slice(i, i + chunkSize);
        chunks.push(chunk);
    }
    return chunks;
}
export function addressFieldsToArray({ addressFirstName, addressLastName, addressOrganization, addressLine1, addressLine2, addressCity, addressStateProvinceRegion, addressPostalCode, countryCode, }) {
    if (countryCode == null) {
        return null;
    }
    return [
        addressOrganization,
        addressFirstName && addressLastName ? `${addressFirstName} ${addressLastName}` : null,
        addressLine1,
        addressLine2,
        `${addressPostalCode} ${addressCity}${addressStateProvinceRegion ? `, ${addressStateProvinceRegion}` : ""}`,
        getCountryName(countryCode),
    ].filter((e) => e != null && e.trim() !== "");
}
